/**
 * Recovery for a stale installed shell: the service worker (or the HTTP cache)
 * is serving an index.html whose hashed scripts are already gone. A normal
 * reload asks that same worker for the same files, so it cannot fix this.
 * Shift+Ctrl+R works because it skips both caches. This does that once,
 * automatically, then stops so a genuinely broken deploy cannot reload forever.
 *
 * The inline copy in `index.html` must stay aligned with this key, window, and
 * URL shape — it runs when the hashed bundles themselves fail to load, so it
 * cannot import this module.
 */
export const SHELL_PURGE_KEY = 'jiopc-shell-purged-at';
export const SHELL_PURGE_WINDOW_MS = 20_000;

export type ShellRecovery = 'purge' | 'show';

/** Purge when online and we have not already done so inside the guard window. */
export function shellRecoveryAction(
  nowMs: number,
  lastPurgeMs: number | null,
  online: boolean,
  windowMs: number = SHELL_PURGE_WINDOW_MS,
): ShellRecovery {
  if (!online) return 'show';
  if (lastPurgeMs === null) return 'purge';
  return nowMs - lastPurgeMs >= windowMs ? 'purge' : 'show';
}

/** Document URL that skips ngsw and the HTTP cache for this navigation only. */
export function cacheBustUrl(href: string, nowMs: number): string {
  const url = new URL(href);
  url.searchParams.set('ngsw-bypass', '1');
  url.searchParams.set('_boot', String(nowMs));
  return url.toString();
}

/** Path to put back in the address bar after a recovered boot. Null when nothing to strip. */
export function stripBootParams(href: string): string | null {
  const url = new URL(href);
  if (!url.searchParams.has('_boot') && !url.searchParams.has('ngsw-bypass')) return null;
  url.searchParams.delete('_boot');
  url.searchParams.delete('ngsw-bypass');
  const search = url.searchParams.toString();
  return url.pathname + (search ? `?${search}` : '') + url.hash;
}

export function readShellPurgeAt(): number | null {
  try {
    const raw = sessionStorage.getItem(SHELL_PURGE_KEY);
    if (!raw || Number.isNaN(Number(raw))) return null;
    return Number(raw);
  } catch {
    return null;
  }
}

/** Drop every service worker and Cache Storage entry for this origin. */
export async function purgeAppShell(): Promise<void> {
  const registrations = navigator.serviceWorker ? await navigator.serviceWorker.getRegistrations() : [];
  await Promise.all(registrations.map((registration) => registration.unregister()));
  if (typeof caches === 'undefined') return;
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
}

/** Navigate to a cache-bypassing URL. Replaces the history entry so Back is not the broken shell. */
export function hardReload(nowMs: number = Date.now()): void {
  window.location.replace(cacheBustUrl(window.location.href, nowMs));
}

/**
 * One automatic hard recovery. `force` is the Refresh button: the user asked
 * again, so do not wait out the guard window. Offline never deletes the only
 * cached copy.
 */
export async function recoverStaleShell(force = false): Promise<void> {
  const online = navigator.onLine;
  const action = force && online ? 'purge' : shellRecoveryAction(Date.now(), readShellPurgeAt(), online);
  if (action === 'show') {
    window.__jiopcShowBootFallback?.();
    return;
  }
  const now = Date.now();
  try {
    sessionStorage.setItem(SHELL_PURGE_KEY, String(now));
  } catch {
    // sessionStorage unavailable — still try to load a clean document.
  }
  try {
    await purgeAppShell();
  } catch {
    // A network navigation is still better than staying on the broken shell.
  }
  hardReload(now);
}

declare global {
  interface Window {
    __jiopcShowBootFallback?: () => void;
    __jiopcRecoverShell?: (force?: boolean) => void;
  }
}
